/**
 * The local-persistence worker (web-client.md §3/§6). It debounce-writes the folded session views +
 * the active session id to the IndexedDB {@link SessionCache} OFF the render path, so a cold start can
 * warm-start read-only until `sys.welcome`. It writes ONLY disposable cache — never a token (§4).
 *
 * DOM-free: it drives its debounce through the injected {@link Timers} seam (the app passes
 * systemTimers, which the browser can back with an idle callback) and persists through the
 * feature-detected cache seam. Deterministic under a fake scheduler.
 */
import type { SessionCache } from '../storage/index.ts';
import type { Store, WarmStartSnapshot } from '../state/index.ts';
import type { Timers, TimerHandle } from '../net/index.ts';
import { systemTimers } from '../net/index.ts';

/** The single cache key holding the warm-start snapshot. */
export const WARM_START_KEY = 'warm-start';

export interface PersistenceOptions {
  timers?: Timers;
  /** Debounce window before a store change is flushed to the cache (default 1 s). */
  debounceMs?: number;
  /** Cap the number of persisted chat lines per session, keeping the write bounded (default 200). */
  maxChatPerSession?: number;
  /** Read the current active session id at flush time. */
  activeId?: () => string | null;
}

export class PersistenceWorker {
  private readonly t: Timers;
  private readonly debounceMs: number;
  private readonly maxChat: number;
  private readonly activeId: () => string | null;

  private unsubscribe: (() => void) | null = null;
  private pending: TimerHandle | null = null;

  constructor(
    private readonly store: Store,
    private readonly cache: SessionCache,
    options: PersistenceOptions = {},
  ) {
    this.t = options.timers ?? systemTimers;
    this.debounceMs = options.debounceMs ?? 1_000;
    this.maxChat = options.maxChatPerSession ?? 200;
    this.activeId = options.activeId ?? (() => null);
  }

  /** Load the last persisted snapshot (cold-start warm start). Null when nothing cached. */
  async load(): Promise<WarmStartSnapshot | null> {
    return this.cache.get<WarmStartSnapshot>(WARM_START_KEY);
  }

  /** Subscribe to the store and begin debounced persistence. */
  start(): void {
    if (this.unsubscribe) return;
    this.unsubscribe = this.store.subscribe(() => this.schedule());
  }

  /** Stop persisting and cancel any pending flush. */
  stop(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    if (this.pending) {
      this.t.clearTimeout(this.pending);
      this.pending = null;
    }
  }

  /** Clear the persisted cache (diagnostics "repair"). */
  async clear(): Promise<void> {
    await this.cache.remove(WARM_START_KEY);
  }

  private schedule(): void {
    if (this.pending) return; // a flush is already queued for this debounce window.
    this.pending = this.t.setTimeout(() => {
      this.pending = null;
      void this.flush();
    }, this.debounceMs);
  }

  /** Serialize the current store views and write them (idempotent; safe to call directly in tests). */
  async flush(): Promise<void> {
    const sessions = this.store.getSessions().map((v) => ({
      ...v,
      chat: v.chat.length > this.maxChat ? v.chat.slice(-this.maxChat) : v.chat,
    }));
    const snapshot: WarmStartSnapshot = {
      sessions,
      activeId: this.activeId(),
      savedAt: this.t.now(),
    };
    await this.cache.put(WARM_START_KEY, snapshot);
  }
}
